import { Injectable } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { MatStepper } from '@angular/material/stepper';
import {
  AppConfigService,
  Constants,
  ContactAttribute,
  ErrorReportingService,
  IApiResult,
  ICountry,
  ILookup,
  LookupType,
  NewAccountEntityType,
  NewAccountType,
  PrsOrganisationType,
  SecurityService,
  strictEmailValidator,
} from 'common';
import { BehaviorSubject, map, Observable } from 'rxjs';
import { IContactDetailsData } from '../interfaces/account-registration/contact-details-data.interface';
import { ILicenceTypeData } from '../interfaces/account-registration/licence-type-data.interface';
import { FinancialDto } from '../models/dtos/account-registration/financial.dto';
import { PrsDto } from '../models/dtos/account-registration/prs.dto';
import { GeneralDetailsDto } from '../models/dtos/account-registration/general-details.dto';
import { ContactDto } from '../models/dtos/account-registration/contact.dto';
import { AccountRegistrationDto } from '../models/dtos/account-registration/account-registration.dto';
import { IGeneralDetailsData } from '../interfaces/account-registration/general-details-data.interface';
import { IFinancialData } from '../interfaces/account-registration/financial-data.interface';
import { IPrsData } from '../interfaces/account-registration/prs-data.interface';
import { HttpClient } from '@angular/common/http';
import { LookupsService } from 'common';
import { PendingAccountRegistration } from '../models/pending-account-registration';

@Injectable({
  providedIn: 'root',
})
export class AccountRegistrationService {
  private prsOrganisationTypes: ILookup[];
  private countries: ICountry[];
  private contactAttributes: ILookup[];

  private numberOfPrsContacts: number = 2;

  private showClubAccountSub = new BehaviorSubject<boolean>(false);
  showClubAccount$ = this.showClubAccountSub.asObservable();

  private showAircraftNoteSub = new BehaviorSubject<boolean>(false);
  showAircraftNote$ = this.showAircraftNoteSub.asObservable();

  private entityTypeSub = new BehaviorSubject<NewAccountEntityType>(NewAccountEntityType.Company);
  entityType$ = this.entityTypeSub.asObservable();

  private numberOfPrsContactsSub = new BehaviorSubject<number>(2);
  numberOfPrsContacts$ = this.numberOfPrsContactsSub.asObservable();

  private prsOrganisationTypeSub = new BehaviorSubject<PrsOrganisationType>(null);
  prsOrganisationType$ = this.prsOrganisationTypeSub.asObservable();

  private accountTypeSub = new BehaviorSubject<NewAccountType>(null);
  accountType$ = this.accountTypeSub.asObservable();

  formGroupAccountType: FormGroup = this.fb.group({
    accountType: ['', [Validators.required]],
  });

  formLicenceType: FormGroup<ILicenceTypeData> = this.fb.group(
    {
      ar: [false],
      er: [false],
      br: [false],
      tb: [false],
      rl: [false],
      sa: [false],
      wtt: [false],
      other: [false],
      isClubAccount: [false],
    },
    { validators: this.checkAtLeastOneCheckbox }
  );

  formGeneralDetails: FormGroup<IGeneralDetailsData> = this.fb.group({
    entityType: ['', [Validators.required]],

    accountName: ['', [Validators.required]],
    officialName: ['', [Validators.required]],
    tradeName: [''],
    prevTradeName: [''],
    orgType: ['', [Validators.required]],
    regNumber: [''],

    salutation: ['', [Validators.required]],
    firstName: ['', [Validators.required]],
    lastName: ['', [Validators.required]],
    email: ['', [Validators.required, strictEmailValidator]],
    phone: ['', [Validators.required]],
    mobile: [''],
    docPrefDeliveryMethod: ['1'],
    docPrefLanguage: ['0'],

    addressLine1: ['', [Validators.required]],
    addressLine2: [''],
    addressLine3: [''],
    addressLine4: [''],
    postCode: [''],
    county: ['', [Validators.required]],
    country: ['', [Validators.required]],
  });

  formFinancialDetails: FormGroup<IFinancialData> = this.fb.group({
    email: ['', [Validators.required, strictEmailValidator]],
    electronicInvoicing: [true],
    phone: ['', [Validators.required]],
    mobile: [''],
    salutation: ['', [Validators.required]],
    firstName: ['', [Validators.required]],
    lastName: ['', [Validators.required]],
    addressLine1: ['', [Validators.required]],
    addressLine2: [''],
    addressLine3: [''],
    addressLine4: [''],
    postCode: [''],
    county: [''],
    country: ['', [Validators.required]],
  });

  formRadioContactDetails: FormGroup<IContactDetailsData> = this.fb.group({
    sameAsOtherContact: [false],
    email: ['', [Validators.required, strictEmailValidator]],
    positionHeld: [''],
    phone: ['', [Validators.required]],
    mobile: [''],
    salutation: ['', [Validators.required]],
    firstName: ['', [Validators.required]],
    lastName: ['', [Validators.required]],
    addressLine1: ['', [Validators.required]],
    addressLine2: [''],
    addressLine3: [''],
    addressLine4: [''],
    postCode: [''],
    county: ['', [Validators.required]],
    country: ['', [Validators.required]],
    dateOfBirth: [''],
    attributes: [new Array<string>()],
  });

  formPrsDetails: FormGroup<IPrsData> = this.fb.group({
    addressLine1: ['', [Validators.required]],
    addressLine2: [''],
    addressLine3: [''],
    addressLine4: [''],
    postCode: [''],
    county: ['', [Validators.required]],
    country: ['', [Validators.required]],
    q1: ['', [Validators.required]],
    q2: ['', [Validators.required]],
    q3: ['', [Validators.required]],
  });

  formPrsContactDetails: Array<FormGroup<IContactDetailsData>> = [
    this.fb.group({
      sameAsOtherContact: [false],
      email: ['', [Validators.required, strictEmailValidator]],
      positionHeld: [''],
      phone: ['', [Validators.required]],
      mobile: [''],
      salutation: ['', [Validators.required]],
      firstName: ['', [Validators.required]],
      lastName: ['', [Validators.required]],
      addressLine1: ['', [Validators.required]],
      addressLine2: [''],
      addressLine3: [''],
      addressLine4: [''],
      postCode: [''],
      county: ['', [Validators.required]],
      country: ['', [Validators.required]],
      dateOfBirth: ['', Validators.required],
      attributes: [new Array<string>()],
    }),
    this.fb.group({
      sameAsOtherContact: [false],
      email: ['', [Validators.required, strictEmailValidator, this.uniqueContactValidator('email', [0])]],
      positionHeld: [''],
      phone: ['', [Validators.required, this.uniqueContactValidator('phone', [0])]],
      mobile: [''],
      salutation: ['', [Validators.required]],
      firstName: ['', [Validators.required]],
      lastName: ['', [Validators.required]],
      addressLine1: ['', [Validators.required]],
      addressLine2: [''],
      addressLine3: [''],
      addressLine4: [''],
      postCode: [''],
      county: ['', [Validators.required]],
      country: ['', [Validators.required]],
      dateOfBirth: ['', Validators.required],
      attributes: [new Array<string>()],
    }),
    this.fb.group({
      sameAsOtherContact: [false],
      email: ['', [Validators.required, strictEmailValidator, this.uniqueContactValidator('email', [0, 1])]],
      positionHeld: [''],
      phone: ['', [Validators.required, this.uniqueContactValidator('phone', [0, 1])]],
      mobile: [''],
      salutation: ['', [Validators.required]],
      firstName: ['', [Validators.required]],
      lastName: ['', [Validators.required]],
      addressLine1: ['', [Validators.required]],
      addressLine2: [''],
      addressLine3: [''],
      addressLine4: [''],
      postCode: [''],
      county: ['', [Validators.required]],
      country: ['', [Validators.required]],
      dateOfBirth: ['', Validators.required],
      attributes: [new Array<string>()],
    }),
  ];

  constructor(
    private readonly fb: FormBuilder,
    private security: SecurityService,
    private httpClient: HttpClient,
    private env: AppConfigService,
    private lookups: LookupsService,
    private errorReporting: ErrorReportingService
  ) {
    this.initialiseFormContactDetails();
    this.initialiseFormFinancialDetails();
    this.initialiseFormGeneralDetails();
    this.initialiseFormGroupLicenceType();
    this.initialiseFormGroupPrsDetails();
    this.initialiseFormPrsContactDetails();
    this.initialiseLookups();
  }

  checkAtLeastOneCheckbox(group: FormGroup) {
    const values = Object.keys(group.controls)
      .filter((key) => key !== 'isClubAccount')
      .map((key) => group.controls[key].value);

    return values.some((v) => v === true) ? null : { atLeastOneRequired: true };
  }

  uniqueContactValidator(controlName: string, contactIndexesToCompare: Array<number>): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      var inValid: boolean = false;
      const controlValue = control.value?.trim().replace(' ', '');

      if (this.formPrsContactDetails) {
        contactIndexesToCompare.forEach((index) => {
          const indexValue = this.formPrsContactDetails[index].get(controlName)?.value?.trim().replace(' ', '');
          inValid = inValid || indexValue === controlValue;
        });
      }

      if (inValid) {
        return { unique: true };
      } else {
        return null;
      }
    };
  }

  previousStep(stepper: MatStepper): void {
    if (stepper.selectedIndex === 1) {
      this.formLicenceType.reset();
      this.setShowClubAccount(false);
      this.setShowAircraftNote(false);
    }

    stepper.previous();
  }

  nextStep(stepper: MatStepper): void {
    if (stepper.selectedIndex === 0) {
      this.formGroupAccountType.markAsDirty();

      //reset all the data in case we have come back through the wizard
      this.initialiseFormContactDetails();
      this.initialiseFormFinancialDetails();
      this.initialiseFormGeneralDetails();
      this.initialiseFormGroupLicenceType();
      this.initialiseFormGroupPrsDetails();
      this.initialiseFormPrsContactDetails();

      this.accountTypeSub.next(this.formGroupAccountType.value.accountType === NewAccountType.Radio ? NewAccountType.Radio : NewAccountType.Prs);
    }

    if (this.formGroupAccountType.value.accountType === NewAccountType.Radio) {
      if (stepper.selectedIndex === 1) {
        this.formLicenceType.markAsDirty();

        this.initialiseFormGeneralDetails();
        if (this.formLicenceType.value.er != null && this.formLicenceType.value.er === true) {
          // Only Amateur Station selected: default to Entity type person
          this.setEntityType(NewAccountEntityType.Person);
        } else {
          // default to Entity type Company
          this.setEntityType(NewAccountEntityType.Company);
        }
      }

      if (stepper.selectedIndex === 2) {
        this.formGeneralDetails.markAsDirty();
        this.formGeneralDetails.markAsTouched();
        if (this.formGeneralDetails.value.entityType === NewAccountEntityType.Person) {
          this.formGeneralDetails.controls.officialName.patchValue(
            ((this.formGeneralDetails.value.firstName ?? '') + ' ' + (this.formGeneralDetails.value.lastName ?? '')).trim()
          );
          this.formGeneralDetails.controls.accountName.patchValue(
            ((this.formGeneralDetails.value.firstName ?? '') + ' ' + (this.formGeneralDetails.value.lastName ?? '')).trim()
          );
          //Financial step is skipped when entity is person and data from general details used for financial
          this.copyGeneralToFinancial();
        }
      }

      if (stepper.selectedIndex === 4) {
        this.formRadioContactDetails.controls.attributes.setValue([ContactAttribute.Regulatory, ContactAttribute.SQU]);
      }
    } else if (this.formGroupAccountType.value.accountType === NewAccountType.Prs) {
      if (stepper.selectedIndex === 2) {
        //Financial step is always skipped and data from general details used for financial
        this.copyGeneralToFinancial();
      }
      if (stepper.selectedIndex === 3) {
        //Contact 1
        this.formPrsContactDetails[0].controls.attributes.setValue([ContactAttribute.Regulatory]);
      }
      if (stepper.selectedIndex === 4) {
        //contact 2
        if (this.formPrsContactDetails[1].controls.sameAsOtherContact.value) {
          this.formPrsContactDetails[0].controls.attributes.setValue([ContactAttribute.Regulatory, ContactAttribute.Director]);
        } else {
          this.formPrsContactDetails[0].controls.attributes.setValue([ContactAttribute.Regulatory]);
          this.formPrsContactDetails[1].controls.attributes.setValue([ContactAttribute.Director]);
        }
      }
      if (stepper.selectedIndex === 5) {
        //contact 3
        this.formPrsContactDetails[2].controls.attributes.setValue([ContactAttribute.Director]);
      }
    }
    stepper.next();
  }

  setEntityType(entityType: NewAccountEntityType) {
    this.formGeneralDetails.controls.entityType.setValue(entityType);
    this.entityTypeSub.next(entityType);
  }

  setShowClubAccount(showClubAccount: boolean) {
    this.showClubAccountSub.next(showClubAccount);
  }

  setShowAircraftNote(showAircraftNote: boolean) {
    this.showAircraftNoteSub.next(showAircraftNote);
  }

  setPrsOrgType(orgType: PrsOrganisationType) {
    this.prsOrganisationTypeSub.next(orgType);
    switch (orgType) {
      case PrsOrganisationType.Partnership:
      case PrsOrganisationType.NotForProfit:
      case PrsOrganisationType.SemiState:
      case PrsOrganisationType.StateOwned:
      case PrsOrganisationType.Charity:
        this.numberOfPrsContacts = 3;
        break;
      default:
        this.numberOfPrsContacts = 2;
        break;
    }
    this.numberOfPrsContactsSub.next(this.numberOfPrsContacts);
  }

  setAccountType(accountType: NewAccountType) {
    this.accountTypeSub.next(accountType);
  }

  setContactSameAsOther(formGroup: FormGroup<IContactDetailsData>, value: boolean) {
    formGroup.controls.sameAsOtherContact.setValue(value);
    value ? formGroup.controls.email.disable() : formGroup.controls.email.enable();
    value ? formGroup.controls.positionHeld.disable() : formGroup.controls.positionHeld.enable();
    value ? formGroup.controls.phone.disable() : formGroup.controls.phone.enable();
    value ? formGroup.controls.mobile.disable() : formGroup.controls.mobile.enable();
    value ? formGroup.controls.salutation.disable() : formGroup.controls.salutation.enable();
    value ? formGroup.controls.firstName.disable() : formGroup.controls.firstName.enable();
    value ? formGroup.controls.lastName.disable() : formGroup.controls.lastName.enable();
    value ? formGroup.controls.addressLine1.disable() : formGroup.controls.addressLine1.enable();
    value ? formGroup.controls.addressLine2.disable() : formGroup.controls.addressLine2.enable();
    value ? formGroup.controls.addressLine3.disable() : formGroup.controls.addressLine3.enable();
    value ? formGroup.controls.addressLine4.disable() : formGroup.controls.addressLine4.enable();
    value ? formGroup.controls.postCode.disable() : formGroup.controls.postCode.enable();
    value ? formGroup.controls.county.disable() : formGroup.controls.county.enable();
    value ? formGroup.controls.country.disable() : formGroup.controls.country.enable();
    value ? formGroup.controls.dateOfBirth.disable() : formGroup.controls.dateOfBirth.enable();
  }

  copyGeneralToFinancial() {
    const generalDetails = this.formGeneralDetails.value;
    this.formFinancialDetails.patchValue(generalDetails);
  }

  copyGeneralToPrs() {
    const generalDetails = this.formGeneralDetails.value;
    this.formPrsDetails.patchValue(generalDetails);
    if (!this.formGeneralDetails.controls.county.value) {
      this.formPrsDetails.controls.county.setValue(undefined);
    }
  }

  copyGeneralToContact(accountType: NewAccountType, contactNo: number = 1) {
    switch (accountType) {
      case NewAccountType.Prs:
        this.formPrsContactDetails[contactNo - 1].patchValue(this.formGeneralDetails.value);
        break;
      case NewAccountType.Radio:
        this.formRadioContactDetails.patchValue(this.formGeneralDetails.value);
        break;
    }
  }

  async submit() {
    var entityType: NewAccountEntityType = <NewAccountEntityType>this.formGeneralDetails.controls.entityType.value;
    var registrationData: AccountRegistrationDto;

    if (this.formGroupAccountType.controls.accountType.value === NewAccountType.Prs) {
      registrationData = new AccountRegistrationDto(
        this.security.profile.username,
        new GeneralDetailsDto(NewAccountType.Prs, this.formGeneralDetails.controls),
        new PrsDto(this.formGeneralDetails.controls, this.formPrsDetails.controls, this.prsOrganisationTypes, this.countries),
        new FinancialDto(this.formFinancialDetails.controls),
        this.contactsDtoArrayFromFormGroups(entityType, this.formPrsContactDetails),
        this.licenceTypesFromFormGroup(this.formLicenceType.controls),
        this.getPrsOrgTypeValueFromKey(this.formGeneralDetails.controls.orgType.value) ?? '',
        this.formGeneralDetails.controls.regNumber.value ?? '',
        this.formGeneralDetails.controls.prevTradeName.value ?? ''
      );
    } else if (this.formGroupAccountType.controls.accountType.value === NewAccountType.Radio) {
      var contact: ContactDto = new ContactDto();
      if (entityType === NewAccountEntityType.Person) {
        this.copyGeneralToContact(this.formGroupAccountType.controls.accountType.value);
      }
      contact.fromContactDetailsData(entityType, this.formRadioContactDetails.controls, this.contactAttributes);

      registrationData = new AccountRegistrationDto(
        this.security.profile.username,
        new GeneralDetailsDto(NewAccountType.Radio, this.formGeneralDetails.controls, this.formLicenceType.controls),
        null,
        new FinancialDto(this.formFinancialDetails.controls),
        [contact],
        this.licenceTypesFromFormGroup(this.formLicenceType.controls),
        undefined,
        undefined,
        undefined
      );
    }

    var primaryContact = registrationData.getContactByEmail(this.security.profile?.user?.email);
    if (primaryContact) {
      //set the primary contact attribute
      primaryContact.primaryContact = true;
    } else {
      //if none of the contacts have the same email address as the user making the application then create a primary onlie user for them
      primaryContact = new ContactDto();
      primaryContact.primaryFromGeneralDetails(entityType, this.formGeneralDetails.controls, this.contactAttributes);
      registrationData.contacts.push(primaryContact);
    }

    this.errorReporting.logDebug(this.getJson(registrationData));

    const url = `${this.env.config.gatewayUrl}/registration/account/request`;
    return await this.httpClient.post(url, registrationData).toPromise();
  }

  getPendingRegistrations(): Observable<Array<PendingAccountRegistration>> {
    const url = `${this.env.config.gatewayUrl}/registration/account/request`;
    return this.httpClient.get(url).pipe(map((data: IApiResult<Array<PendingAccountRegistration>>) => data.value));
  }

  countryIsEu(countryName: string): boolean {
    var countryDetails = this.countries.filter((country: ICountry) => {
      return country.name.toLowerCase() === countryName.toLowerCase();
    });
    return countryDetails && countryDetails.length > 0 ? countryDetails[0].isEU : false;
  }

  private initialiseFormContactDetails() {
    this.formRadioContactDetails.reset({ country: Constants.irelandCountryString, attributes: new Array<string>() });
    this.formRadioContactDetails.controls.county.enable();
    this.formRadioContactDetails.markAsPristine();
    this.formRadioContactDetails.markAsUntouched();
  }

  private initialiseFormFinancialDetails() {
    this.formFinancialDetails.reset({ electronicInvoicing: true, country: Constants.irelandCountryString });
    this.formFinancialDetails.controls.county.enable();
    this.formFinancialDetails.markAsPristine();
    this.formFinancialDetails.markAsUntouched();
  }

  private initialiseFormGeneralDetails() {
    this.formGeneralDetails.reset({
      entityType: NewAccountEntityType.Company,
      docPrefDeliveryMethod: '1',
      docPrefLanguage: '0',
      country: Constants.irelandCountryString,
      email: this.security.profile?.user?.email,
    });
    this.formGeneralDetails.controls.county.enable();
    this.formGeneralDetails.markAsPristine();
    this.formGeneralDetails.markAsUntouched();
  }

  private initialiseFormGroupLicenceType() {
    this.formLicenceType.reset({
      ar: false,
      br: false,
      er: false,
      isClubAccount: false,
      other: false,
      rl: false,
      sa: false,
      tb: false,
      wtt: false,
    });
    this.formLicenceType.markAsPristine();
    this.formLicenceType.markAsUntouched();
  }

  private initialiseFormGroupPrsDetails() {
    this.formPrsDetails.reset({ country: Constants.irelandCountryString });
    this.formPrsDetails.controls.county.enable();
    this.formPrsDetails.markAsPristine();
    this.formPrsDetails.markAsUntouched();
  }

  private initialiseFormPrsContactDetails() {
    this.formPrsContactDetails.forEach((prsContactDetails) => {
      prsContactDetails.reset({ country: Constants.irelandCountryString, attributes: new Array<string>() });
      prsContactDetails.controls.county.enable();
      prsContactDetails.markAsPristine();
      prsContactDetails.markAsUntouched();
    });
  }

  private initialiseLookups() {
    this.lookups.getPRSOrganisationTypes().subscribe((data) => {
      this.prsOrganisationTypes = data;
    });

    this.lookups.getCountries().subscribe((data) => {
      this.countries = data;
    });

    this.lookups.getListByType(LookupType.contactAttribute).subscribe((data) => {
      this.contactAttributes = data;
    });
  }

  private contactsDtoArrayFromFormGroups(
    entityType: NewAccountEntityType,
    contactDetailsArray: Array<FormGroup<IContactDetailsData>>
  ): Array<ContactDto> {
    var contacts: Array<ContactDto> = new Array<ContactDto>();

    for (let contactIndex = 0; contactIndex < this.numberOfPrsContacts; contactIndex++) {
      if (!contactDetailsArray[contactIndex].controls.sameAsOtherContact.value) {
        var newContact: ContactDto = new ContactDto();
        newContact.fromContactDetailsData(entityType, contactDetailsArray[contactIndex].controls, this.contactAttributes);
        contacts.push(newContact);
      }
    }

    return contacts;
  }

  private licenceTypesFromFormGroup(licenceTypeData: ILicenceTypeData): Array<string> {
    var licenceTypes: Array<string> = new Array<string>();

    if (licenceTypeData.ar.value) {
      licenceTypes.push('ar');
    }
    if (licenceTypeData.br.value) {
      licenceTypes.push('br');
    }
    if (licenceTypeData.er.value) {
      licenceTypes.push('er');
    }
    if (licenceTypeData.other.value) {
      licenceTypes.push('other');
    }
    if (licenceTypeData.rl.value) {
      licenceTypes.push('rl');
    }
    if (licenceTypeData.sa.value) {
      licenceTypes.push('sa');
    }
    if (licenceTypeData.tb.value) {
      licenceTypes.push('tb');
    }
    if (licenceTypeData.wtt.value) {
      licenceTypes.push('wtt');
    }

    return licenceTypes;
  }

  private getPrsOrgTypeValueFromKey(key: string): string {
    var result = this.prsOrganisationTypes.filter((lookup: ILookup) => {
      return lookup.key === key;
    });
    return result && result.length > 0 ? result[0].value : null;
  }

  private getJson(registrationData: AccountRegistrationDto): string {
    return JSON.stringify(registrationData, (key, value) => {
      //There are extra "private" properties that get included in the JSON and are just noise so we can strip them out here1
      switch (key.toLowerCase()) {
        case 'orgtypes':
        case 'countries':
        case 'contactattributes':
          return undefined;
        default:
          return value;
      }
    });
  }
}
